/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { getCmdsForLine } from '../utils/PRIVATE_chartUtils/utils';
import { Point } from '../utils/PRIVATE_visSVGUtils';
import { rgba } from '../UNSAFE_Theme';
import { colorSchemeVars } from '../Common/themes/themeContract.css';
import { styles } from '../UNSAFE_Chart/themes/ChartStyles.css';
import { Property } from 'csstype';

type LineSeriesProps = {
  color: ColorProps['color'];
  lineStyle?: 'dotted' | 'dashed' | 'solid';
  lineColor?: Property.Color;
  lineWidth?: number;
  lineType?: 'straight' | 'curved';
  isHorizontal: boolean;
  lineSegmentCoords: Point[][];
  isHighlighted: boolean;
  seriesIndex: number;
};

export function LineSeries({
  color,
  isHorizontal,
  lineType = 'straight',
  lineColor,
  lineStyle,
  lineWidth = 3,
  isHighlighted,
  lineSegmentCoords,
  seriesIndex
}: LineSeriesProps) {
  const style = {
    stroke: lineColor || color,
    strokeDasharray: lineStyle === 'dotted' ? 3 : lineStyle === 'dashed' ? 6 : undefined,
    strokeWidth: lineWidth,
    fill: rgba(colorSchemeVars.palette.neutral[0], 0)
  };

  return (
    <>
      {lineSegmentCoords.map((value: Point[]) => {
        const cmds = getCmdsForLine(value, lineType, isHorizontal);
        return lineType === 'straight' ? (
          <polyline
            points={cmds}
            style={style}
            className={isHighlighted ? undefined : styles.dimmed}
            data-oj-series-index={seriesIndex}
            data-oj-object={'line'}
          />
        ) : (
          <path
            d={cmds}
            style={style}
            className={isHighlighted ? undefined : styles.dimmed}
            data-oj-series-index={seriesIndex}
            data-oj-object={'line'}
          />
        );
      })}
    </>
  );
}
